import { AlertTriangle, Hand } from 'lucide-react';

import { cx } from '@/lib/cx';
import { Contador } from '@/ui/components/contador';
import { Dinheiro } from '@/ui/components/dinheiro';
import { Skeleton } from '@/ui/primitives/skeleton';
import { type PreviaRegra as Previa } from '@fiscoai/contracts';

import { liquidoDaPrevia } from './formulario-regra';
import { formatarNcm, itens } from './textos';

interface Props {
  previa: Previa | undefined;
  condicoesValidas: boolean;
  carregando: boolean;
  erro: string | null;
  ativa: boolean;
}

/** O que o leitor de tela ouve (o número visível conta animado; aqui vai só o final). */
function anuncio(p: Props): string {
  if (!p.condicoesValidas || !p.previa) return '';
  const n = liquidoDaPrevia(p.previa);
  return p.ativa ? `Classificaria ${itens(n)}` : `Desligada. Ligada, classificaria ${itens(n)}`;
}

function Avisos({ previa }: { previa: Previa }) {
  if (previa.pegosPorRegraAcima === 0 && previa.jaManuais === 0) return null;
  return (
    <ul className="grid gap-1 text-xs text-texto-suave">
      {previa.pegosPorRegraAcima > 0 && (
        <li className="flex items-start gap-1.5">
          <AlertTriangle className="mt-0.5 size-3.5 shrink-0 text-alerta" aria-hidden />
          {itens(previa.pegosPorRegraAcima)}{' '}
          {previa.pegosPorRegraAcima === 1 ? 'já é pego' : 'já são pegos'} por uma regra que vem
          antes.
        </li>
      )}
      {previa.jaManuais > 0 && (
        <li className="flex items-start gap-1.5">
          <Hand className="mt-0.5 size-3.5 shrink-0" aria-hidden />
          {itens(previa.jaManuais)}{' '}
          {previa.jaManuais === 1
            ? 'foi classificado à mão e continua como está.'
            : 'foram classificados à mão e continuam como estão.'}
        </li>
      )}
    </ul>
  );
}

function Exemplos({ previa }: { previa: Previa }) {
  if (previa.exemplos.length === 0) return null;
  return (
    <ul aria-label="Exemplos de itens" className="grid divide-y divide-borda">
      {previa.exemplos.map((e, i) => (
        <li key={`${e.descricao}-${String(i)}`} className="grid gap-0.5 py-2 first:pt-0 last:pb-0">
          <span className="flex items-baseline justify-between gap-3 text-sm">
            <span className="min-w-0 truncate font-medium">{e.descricao}</span>
            <Dinheiro valor={e.valor} className="shrink-0" />
          </span>
          <span className="flex flex-wrap gap-x-3 text-xs text-texto-fraco">
            {e.emitente && <span className="min-w-0 truncate">{e.emitente}</span>}
            {e.ncm && (
              <span>
                NCM <span className="font-mono">{formatarNcm(e.ncm)}</span>
              </span>
            )}
            <span>
              CFOP <span className="font-mono">{e.cfop}</span>
            </span>
          </span>
        </li>
      ))}
    </ul>
  );
}

function Corpo(p: Props) {
  if (!p.condicoesValidas)
    return (
      <p className="text-sm text-texto-suave">
        Informe uma condição para ver quais itens já importados a regra pegaria.
      </p>
    );
  if (p.erro) return <p className="text-sm text-perigo">{p.erro}</p>;
  if (!p.previa) return <Skeleton className="h-24 rounded-controle" />;
  const n = liquidoDaPrevia(p.previa);
  return (
    <div className={cx('grid gap-3', p.carregando && 'opacity-60')}>
      <p className="grid text-sm text-texto-suave" aria-hidden>
        <span>{p.ativa ? 'Classificaria' : 'Desligada. Ligada, classificaria'}</span>
        <span className="flex items-baseline gap-1.5">
          <Contador valor={n} className="text-3xl font-semibold text-marca" />
          {n === 1 ? 'item já importado' : 'itens já importados'}
        </span>
      </p>
      {p.previa.itens === 0 && (
        <p className="text-xs text-texto-fraco">
          Nenhum item importado casa com estas condições. A regra vale para as próximas notas.
        </p>
      )}
      <Avisos previa={p.previa} />
      <Exemplos previa={p.previa} />
    </div>
  );
}

/**
 * Prévia ao vivo da regra em edição, nos itens de entrada já importados: quantos ela
 * classificaria, os que outra regra pega antes, os manuais (que não mudam) e três exemplos.
 */
export function PreviaRegra(p: Props) {
  return (
    <section
      aria-label="Prévia da regra"
      aria-busy={p.carregando}
      className="grid content-start gap-3 self-start rounded-cartao border border-borda bg-superficie p-3"
    >
      <h3 className="text-sm font-medium">Prévia nos itens já importados</h3>
      <p className="sr-only" aria-live="polite" aria-atomic>
        {anuncio(p)}
      </p>
      <Corpo {...p} />
    </section>
  );
}
